/**
 * Core PDF processing engine.
 * Uses PDF.js loaded via CDN (injected into window by loadPdfJs).
 * Reads PDFs from a FileSystemDirectoryHandle, extracts names, writes renamed copies.
 *
 * Backend integration point: replace File System Access API calls with server-side
 * file handling if deploying in a non-browser environment.
 */

import JSZip from 'jszip';
import { extractName, sanitizeFilename } from './nameExtractor';
import type { ProcessPdfsOptions, ProcessPdfsFromFilesOptions, ProcessingResult, ProcessingStats } from './types';

declare global {
  interface Window {
    pdfjsLib: any;
  }
}

/** Dynamically loads PDF.js from CDN if not already loaded */
async function loadPdfJs(): Promise<void> {
  if (typeof window !== 'undefined' && window.pdfjsLib) return;

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src =
      'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
    script.onload = () => {
      if (window.pdfjsLib) {
        window.pdfjsLib.GlobalWorkerOptions.workerSrc =
          'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
        resolve();
      } else {
        reject(new Error('PDF.js não carregou corretamente.'));
      }
    };
    script.onerror = () => reject(new Error('Falha ao carregar PDF.js da CDN.'));
    document.head.appendChild(script);
  });
}

/** Extracts all text content from a PDF ArrayBuffer using PDF.js */
async function extractTextFromPdf(buffer: ArrayBuffer): Promise<{ text: string; pageCount: number }> {
  const pdfjsLib = window.pdfjsLib;
  // Slice a copy — PDF.js transfers (detaches) the original ArrayBuffer
  const loadingTask = pdfjsLib.getDocument({ data: buffer.slice(0) });
  const pdf = await loadingTask.promise;
  let pageCount = pdf.numPages;

  // Only read the first page — the name field is always on page 1
  const page = await pdf.getPage(1);
  const content = await page.getTextContent();
  let text = content.items
    .map((item: any) => item.str)
    .join(' ');

  return { text, pageCount };
}

/** Generates a unique filename in the output directory, adding suffix if needed */
async function resolveOutputFilename(
  outDir: FileSystemDirectoryHandle,
  baseName: string
): Promise<string> {
  const sanitized = sanitizeFilename(baseName);
  let candidate = `${sanitized}.PDF`;

  try {
    // Check if file exists — getFileHandle throws if not found
    await outDir.getFileHandle(candidate, { create: false });
    // File exists — add numeric suffix
    let counter = 2;
    while (counter <= 99) {
      candidate = `${sanitized} (${counter}).PDF`;
      try {
        await outDir.getFileHandle(candidate, { create: false });
        counter++;
      } catch {
        break; // This name is available
      }
    }
  } catch {
    // File doesn't exist — candidate is safe to use
  }

  return candidate;
}

/** Reads all .pdf files from a directory handle */
async function collectPdfFiles(
  dirHandle: FileSystemDirectoryHandle
): Promise<{ name: string; handle: FileSystemFileHandle }[]> {
  const files: { name: string; handle: FileSystemFileHandle }[] = [];

  // Backend integration point: replace with server-side directory listing
  for await (const [name, handle] of (dirHandle as any).entries()) {
    if (
      handle.kind === 'file' && name.toLowerCase().endsWith('.pdf')
    ) {
      files.push({ name, handle: handle as FileSystemFileHandle });
    }
  }

  return files;
}

/** Main processing function */
export async function processPdfs(options: ProcessPdfsOptions): Promise<void> {
  const { dirHandle, abortRef, onProgress, onResult, onDone, onError } = options;

  const stats: ProcessingStats = { total: 0, renamed: 0, warnings: 0, errors: 0 };

  try {
    await loadPdfJs();
  } catch (err: any) {
    onError(`Não foi possível carregar PDF.js: ${err?.message ?? 'Erro desconhecido'}`);
    return;
  }

  let pdfFiles: { name: string; handle: FileSystemFileHandle }[];
  try {
    pdfFiles = await collectPdfFiles(dirHandle);
  } catch (err: any) {
    onError(`Erro ao ler a pasta: ${err?.message ?? 'Erro desconhecido'}`);
    return;
  }

  if (pdfFiles.length === 0) {
    onError('Nenhum ficheiro PDF encontrado na pasta selecionada.');
    return;
  }

  stats.total = pdfFiles.length;

  // Create or access "saida" subdirectory
  let outDir: FileSystemDirectoryHandle;
  try {
    outDir = await dirHandle.getDirectoryHandle('saida', { create: true });
  } catch (err: any) {
    onError(`Não foi possível criar a pasta "saida": ${err?.message ?? 'Erro desconhecido'}`);
    return;
  }

  for (let i = 0; i < pdfFiles.length; i++) {
    if (abortRef.current) break;

    const { name: originalName, handle: fileHandle } = pdfFiles[i];
    onProgress(i, pdfFiles.length);

    const resultId = `result-${i + 1}`;

    try {
      // Read file as ArrayBuffer
      const file = await fileHandle.getFile();
      const buffer = await file.arrayBuffer();

      // Extract text with PDF.js
      let text: string;
      let pageCount: number;
      try {
        const extracted = await extractTextFromPdf(buffer);
        text = extracted.text;
        pageCount = extracted.pageCount;
      } catch (pdfErr: any) {
        const result: ProcessingResult = {
          id: resultId,
          originalName,
          extractedName: null,
          outputName: null,
          status: 'error',
          message: `Falha ao ler o PDF: ${pdfErr?.message ?? 'Ficheiro corrompido ou ilegível'}`,
        };
        onResult(result);
        stats.errors++;
        continue;
      }

      // Extract name
      const extractedName = extractName(text);

      if (!extractedName) {
        const result: ProcessingResult = {
          id: resultId,
          originalName,
          extractedName: null,
          outputName: null,
          status: 'warning',
          message:
            'Campo "Nome" não encontrado no documento. O ficheiro não foi copiado. Verifique se é um comprovativo da Segurança Social.',
          pageCount,
        };
        onResult(result);
        stats.warnings++;
        continue;
      }

      // Resolve output filename (handles duplicates)
      const outputName = await resolveOutputFilename(outDir, extractedName);

      // Write renamed copy to "saida"
      try {
        const outFileHandle = await outDir.getFileHandle(outputName, { create: true });
        const writable = await outFileHandle.createWritable();
        await writable.write(buffer);
        await writable.close();
      } catch (writeErr: any) {
        const result: ProcessingResult = {
          id: resultId,
          originalName,
          extractedName,
          outputName: null,
          status: 'error',
          message: `Nome extraído com sucesso, mas falhou ao escrever o ficheiro: ${writeErr?.message ?? 'Erro de escrita'}`,
          pageCount,
        };
        onResult(result);
        stats.errors++;
        continue;
      }

      const result: ProcessingResult = {
        id: resultId,
        originalName,
        extractedName,
        outputName,
        status: 'success',
        pageCount,
      };
      onResult(result);
      stats.renamed++;
    } catch (err: any) {
      const result: ProcessingResult = {
        id: resultId,
        originalName,
        extractedName: null,
        outputName: null,
        status: 'error',
        message: `Erro inesperado: ${err?.message ?? 'Erro desconhecido'}`,
      };
      onResult(result);
      stats.errors++;
    }
  }

  onProgress(pdfFiles.length, pdfFiles.length);
  onDone({ ...stats, total: pdfFiles.length });
}

/** Main processing function using File[] (from <input webkitdirectory>) */
export async function processPdfsFromFiles(options: ProcessPdfsFromFilesOptions): Promise<void> {
  const { files, abortRef, onProgress, onResult, onDone, onError } = options;

  const stats: ProcessingStats = { total: 0, renamed: 0, warnings: 0, errors: 0 };

  try {
    await loadPdfJs();
  } catch (err: any) {
    onError(`Não foi possível carregar PDF.js: ${err?.message ?? 'Erro desconhecido'}`);
    return;
  }

  if (files.length === 0) {
    onError('Nenhum ficheiro PDF encontrado na pasta selecionada.');
    return;
  }

  stats.total = files.length;

  // Track used output names to avoid duplicates
  const usedNames = new Set<string>();

  function resolveOutputName(baseName: string): string {
    const sanitized = sanitizeFilename(baseName);
    let candidate = `${sanitized}.PDF`;
    if (!usedNames.has(candidate)) {
      usedNames.add(candidate);
      return candidate;
    }
    let counter = 2;
    while (counter <= 99) {
      candidate = `${sanitized} (${counter}).PDF`;
      if (!usedNames.has(candidate)) {
        usedNames.add(candidate);
        return candidate;
      }
      counter++;
    }
    return candidate;
  }

  // ZIP archive — all renamed PDFs go inside a "saida/" folder within the ZIP
  const zip = new JSZip();
  const saida = zip.folder('saida')!;
  let filesAddedToZip = 0;

  for (let i = 0; i < files.length; i++) {
    if (abortRef.current) break;

    const file = files[i];
    const originalName = file.name;
    onProgress(i, files.length);

    const resultId = `result-${i + 1}`;

    try {
      const buffer = await file.arrayBuffer();

      // Keep a separate copy for ZIP — PDF.js may detach the original ArrayBuffer
      const bufferForZip = buffer.slice(0);

      let text: string;
      let pageCount: number;
      try {
        const extracted = await extractTextFromPdf(buffer);
        text = extracted.text;
        pageCount = extracted.pageCount;
      } catch (pdfErr: any) {
        const result: ProcessingResult = {
          id: resultId,
          originalName,
          extractedName: null,
          outputName: null,
          status: 'error',
          message: `Falha ao ler o PDF: ${pdfErr?.message ?? 'Ficheiro corrompido ou ilegível'}`,
        };
        onResult(result);
        stats.errors++;
        continue;
      }

      const extractedName = extractName(text);

      if (!extractedName) {
        // No name found — add to ZIP using the original filename so it's not lost
        const fallbackName = originalName.toUpperCase().endsWith('.PDF')
          ? originalName
          : originalName.replace(/\.pdf$/i, '.PDF');
        saida.file(fallbackName, bufferForZip);
        filesAddedToZip++;

        const result: ProcessingResult = {
          id: resultId,
          originalName,
          extractedName: null,
          outputName: fallbackName,
          status: 'warning',
          message:
            'Campo "Nome" não encontrado. Ficheiro incluído na pasta "saida" com o nome original.',
          pageCount,
        };
        onResult(result);
        stats.warnings++;
        continue;
      }

      const outputName = resolveOutputName(extractedName);

      // Add renamed PDF to the saida/ folder inside the ZIP
      saida.file(outputName, bufferForZip);
      filesAddedToZip++;

      const result: ProcessingResult = {
        id: resultId,
        originalName,
        extractedName,
        outputName,
        status: 'success',
        pageCount,
      };
      onResult(result);
      stats.renamed++;
    } catch (err: any) {
      const result: ProcessingResult = {
        id: resultId,
        originalName,
        extractedName: null,
        outputName: null,
        status: 'error',
        message: `Erro inesperado: ${err?.message ?? 'Erro desconhecido'}`,
      };
      onResult(result);
      stats.errors++;
    }
  }

  onProgress(files.length, files.length);

  // Generate and download the ZIP if at least one file was added (renamed or original)
  if (filesAddedToZip > 0 && !abortRef.current) {
    try {
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(zipBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'saida.zip';
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 15000);
    } catch (zipErr: any) {
      onError(`Erro ao gerar o ficheiro ZIP: ${zipErr?.message ?? 'Erro desconhecido'}`);
    }
  }

  onDone({ ...stats, total: files.length });
}